// Estrutura das telas. Três layouts com a mesma interface (P0.10):
//  - desktop (Tauri): janela estreita, navegação pelos ícones do cabeçalho
//  - web larga (≥ 768 px — ASM-080): barra lateral verde + lista + painel de foco
//  - celular (< 768 px): uma coluna + barra de abas no rodapé
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { buildBoard } from '../domain/ordering';
import { toDateKey } from '../domain/dates';
import { formatLongDate } from '../i18n/format';
import { useAppState, useStore } from '../state/AppStore';
import { CELEBRATE_MS } from '../state/floatController';
import { NavContext, useNav, useServices, type View } from '../state/services';
import { usePomodoro } from '../state/usePomodoro';
import { useAccess } from '../state/useSubscription';
import { useNow } from '../state/useNow';
import { AuthScreens, NewPasswordScreen } from './auth/AuthScreens';
import { BreakScreen } from './BreakScreen';
import { CelebrationStage, DayDoneDialog, DoneFlash, useCelebrationOn } from './Celebration';
import { CurrentTask } from './CurrentTask';
import { DoneSection } from './DoneSection';
import { HeaderTitle } from './HeaderTitle';
import { TrialEndedDialog } from './TrialEndedDialog';
import { BeeLogo, Icon } from './Icon';
import { MobileTabBar } from './MobileTabBar';
import { NewTaskForm, cls } from './NewTaskForm';
import { InstallAppBanner } from './InstallAppBanner';
import { OfflineBanner } from './OfflineBanner';
import { Onboarding } from './Onboarding';
import { Paywall } from './Paywall';
import { PomodoroToggle } from './PomodoroToggle';
import { ReadOnlyBanner } from './ReadOnlyBanner';
import { SettingsScreen } from './SettingsScreen';
import { Sidebar } from './Sidebar';
import { TaskList } from './TaskList';
import { Toast } from './Toast';

export const WIDE_QUERY = '(min-width: 768px)';

export function useIsWide(): boolean {
  const get = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(WIDE_QUERY).matches : true);
  const [wide, setWide] = useState(get);
  useEffect(() => {
    const mq = window.matchMedia?.(WIDE_QUERY);
    if (!mq) return;
    const onChange = () => setWide(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  return wide;
}

export type Layout = 'desktop' | 'wide' | 'mobile';

/**
 * Contador do topo (AC-191): com tarefas para hoje (hoje + atrasadas), conta só elas —
 * "feitas hoje"; sem nenhuma para hoje, conta as concluídas hoje e as abertas sem data.
 */
function Progress() {
  const { t } = useTranslation();
  const tasks = useAppState((s) => s.tasks);
  const now = useNow(30_000);
  const today = toDateKey(new Date(now));
  const board = buildBoard(tasks, new Date(now));
  const forToday = (x: { due_date: string | null }) => !!x.due_date && x.due_date <= today;
  const todayDone = board.doneToday.filter(forToday).length;
  const todayTotal = todayDone + tasks.filter((x) => x.status !== 'done' && forToday(x)).length;
  const onlyToday = todayTotal > 0;
  const done = onlyToday ? todayDone : board.doneToday.length;
  const total = onlyToday ? todayTotal : done + tasks.filter((x) => x.status !== 'done' && (!x.due_date || x.due_date <= today)).length;
  if (!total) return null;
  const label = t(onlyToday ? 'header.progressToday' : 'header.progress', { done, total });
  return (
    <div className="progress" aria-label={label}>
      <div className="progress__track">
        <div className="progress__fill" style={{ width: `${(done / total) * 100}%` }} />
      </div>
      <span className="progress__label">{label}</span>
    </div>
  );
}

function Header({ layout }: { layout: Layout }) {
  const { t, i18n } = useTranslation();
  const nav = useNav();
  const services = useServices();
  const now = useNow(60_000);

  if (layout === 'wide') {
    return (
      <header className="page-head">
        <div>
          <HeaderTitle className="page-title" />
          <p className="page-sub">{formatLongDate(new Date(now), i18n.language)}</p>
        </div>
        <div className="page-head__actions">
          <PomodoroToggle />
          {services.desktop && (
            <button type="button" className="btn btn--outline" onClick={() => void services.float.openFloat()}>
              <Icon name="pip" size={14} />
              {t('header.miniWindow')}
            </button>
          )}
        </div>
      </header>
    );
  }

  return (
    <header className="win-head">
      <span className="win-head__logo">
        <BeeLogo size={28} />
      </span>
      <HeaderTitle className="win-head__title" />
      <div className="win-head__icons">
        <PomodoroToggle />
        <button type="button" className="icon-btn" aria-label={t('nav.settings')} onClick={() => nav.go('settings')}>
          <Icon name="settings" size={18} />
        </button>
        {services.desktop && (
          <button type="button" className="icon-btn" aria-label={t('header.miniWindow')} onClick={() => void services.float.openFloat()}>
            <Icon name="shrink" size={18} />
          </button>
        )}
      </div>
    </header>
  );
}

// A janela principal tem o foco? (minimizada pela barra flutuante, perde o foco)
let windowFocused = typeof document !== 'undefined' && document.hasFocus();
const focusListeners = new Set<() => void>();
if (typeof window !== 'undefined') {
  const set = (v: boolean) => () => {
    windowFocused = v;
    focusListeners.forEach((l) => l());
  };
  window.addEventListener('focus', set(true));
  window.addEventListener('blur', set(false));
}
function onWindowFocus(listener: () => void) {
  focusListeners.add(listener);
  return () => void focusListeners.delete(listener);
}

/**
 * A lista está na frente: a barra flutuante fechada, ou a janela principal com o foco
 * (aberta por cima da barra). Com a lista minimizada, comemoração e pop-up de fim do
 * dia esperam ela voltar (AC-190).
 */
function useListInFront(): boolean {
  const { float } = useServices();
  const subscribe = useCallback(
    (l: () => void) => {
      const offFloat = float.subscribe(l);
      const offFocus = onWindowFocus(l);
      return () => {
        offFloat();
        offFocus();
      };
    },
    [float]
  );
  const get = useCallback(() => !float.isVisible() || windowFocused, [float]);
  return useSyncExternalStore(subscribe, get, get);
}

/** Topo da lista: comemoração, hora de relaxar, pausa do Pomodoro ou a tarefa atual. */
function FocusSlot({ large = false }: { large?: boolean }) {
  const tasks = useAppState((s) => s.tasks);
  const celebration = useAppState((s) => s.celebration);
  const loud = useCelebrationOn();
  const store = useStore();
  const { state: pomo } = usePomodoro();
  const access = useAccess();
  const now = useNow();
  const inFront = useListInFront();
  const { float } = useServices();
  const board = buildBoard(tasks, new Date(now));
  // concluída com a barra flutuante aberta: a comemoração (meme) é só da barra; a lista não repete (AC-190)
  const inFloat = useRef<{ taskId: string | null; shown: boolean }>({ taskId: null, shown: false });
  if (celebration && !celebration.endOfDay && inFloat.current.taskId !== celebration.taskId) {
    inFloat.current = { taskId: celebration.taskId, shown: float.isVisible() };
  }
  const floatCelebrated = !!celebration && !celebration.endOfDay && inFloat.current.shown;
  useEffect(() => {
    if (!floatCelebrated) return;
    const id = setTimeout(() => store.dismissCelebration(), CELEBRATE_MS);
    return () => clearTimeout(id);
  }, [floatCelebrated, celebration?.taskId, store]);
  const showCelebration = !!celebration && !celebration.endOfDay && !board.current && inFront && !floatCelebrated;
  // comemoração desligada na hora de relaxar: vai direto ao "Vamos relaxar" (AC-230)
  const skipQuiet = showCelebration && !loud && pomo.phase === 'ready';
  useEffect(() => {
    if (skipQuiet) store.dismissCelebration();
  }, [skipQuiet, store]);
  if (pomo.phase === 'break' || pomo.phase === 'breakOver') return <BreakScreen canEdit={access.canEdit} />;
  if (showCelebration && !loud && pomo.phase !== 'ready') return <DoneFlash data={celebration} />;
  if (showCelebration || pomo.phase === 'ready') {
    return <CelebrationStage celebration={showCelebration && loud ? celebration : null} ready={pomo.phase === 'ready'} canEdit={access.canEdit} />;
  }
  if (board.current) return <CurrentTask task={board.current} canEdit={access.canEdit} large={large} />;
  return null;
}

/**
 * Funil de vendas (AC-208): assinatura encerrada → pop-up a cada abertura do app (e a cada
 * login, porque a tela entra de novo). Fechar vale até a próxima abertura. Quem nunca assinou
 * não vê o pop-up: abre direto nos planos (AC-207, usePlansFirst).
 */
function SubscriptionEnded() {
  const access = useAccess();
  const nav = useNav();
  const [closed, setClosed] = useState(false);
  if (!access.subscriptionEnded || closed || nav.view === 'subscription') return null;
  return (
    <TrialEndedDialog
      onClose={() => setClosed(true)}
      onPlans={() => {
        setClosed(true);
        nav.go('subscription');
      }}
    />
  );
}

/** Pop-up de fim do dia (AC-030) — com a lista minimizada, espera ela voltar para a frente. */
function DayDone() {
  const endOfDay = useAppState((s) => !!s.celebration?.endOfDay);
  const access = useAccess();
  const inFront = useListInFront();
  // depois de aparecer, fica até a pessoa fechar (mesmo se a lista perder o foco)
  const [shown, setShown] = useState(false);
  useEffect(() => setShown(endOfDay && (shown || inFront)), [endOfDay, inFront, shown]);
  return endOfDay && (inFront || shown) ? <DayDoneDialog canEdit={access.canEdit} /> : null;
}

function TasksView({ layout }: { layout: Layout }) {
  const { t } = useTranslation();
  const access = useAccess();
  if (layout === 'wide') {
    return (
      <div className="tasks-view tasks-view--wide">
        <Header layout={layout} />
        <Progress />
        <ReadOnlyBanner />
        <div className="columns">
          <div className="columns__main">
            <NewTaskForm disabled={!access.canEdit} />
            <TaskList canEdit={access.canEdit} />
          </div>
          <aside className="columns__side">
            <FocusSlot large />
            <div className="tip">
              <Icon name="pip" size={18} />
              <span>{t('web.tabTip')}</span>
            </div>
            <DoneSection />
          </aside>
        </div>
      </div>
    );
  }
  return (
    <div className="tasks-view">
      <Header layout={layout} />
      <Progress />
      <ReadOnlyBanner />
      <NewTaskForm disabled={!access.canEdit} />
      <FocusSlot />
      <TaskList canEdit={access.canEdit} />
      <DoneSection />
    </div>
  );
}

function FocusView() {
  const { t } = useTranslation();
  const tasks = useAppState((s) => s.tasks);
  const store = useStore();
  const access = useAccess();
  const now = useNow();
  const board = buildBoard(tasks, new Date(now));
  const { state: pomo } = usePomodoro();
  const hasSlot = !!board.current || pomo.phase === 'ready' || pomo.phase === 'break' || pomo.phase === 'breakOver';
  return (
    <div className="focus-view">
      <header className="settings__head">
        <h1 className="screen-title">{t('nav.focus')}</h1>
        <PomodoroToggle />
      </header>
      <FocusSlot large />
      {!hasSlot && (
        <div className="empty">
          <p className="empty__title">{t('focus.nothing')}</p>
          {board.next && (
            <button type="button" className="btn btn--primary" disabled={!access.canEdit} onClick={() => void store.start(board.next!.id)}>
              <Icon name="play" size={12} filled />
              {t('focus.startNext', { title: board.next.title })}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Sem teste grátis: quem nunca assinou abre direto nos planos, a cada abertura e a cada login (AC-207). */
function usePlansFirst(go: (view: View) => void) {
  const ready = useAppState((s) => s.phase === 'ready');
  const userId = useAppState((s) => s.session?.user.id ?? null);
  const access = useAccess();
  const routed = useRef<string | null>(null);
  const gate = access.neverSubscribed && !access.hasAccess;
  useEffect(() => {
    if (!ready || !userId) {
      routed.current = null;
      return;
    }
    if (routed.current === userId) return;
    routed.current = userId;
    if (gate) go('subscription');
  }, [ready, userId, gate, go]);
}

/** Pagou com a tela de planos aberta: vai direto para a lista, já liberada (AC-103). */
function useLeavePlansWhenPaid(view: View, go: (view: View) => void) {
  const paying = useAccess().paying;
  const wasPaying = useRef(paying);
  useEffect(() => {
    if (paying && !wasPaying.current && view === 'subscription') go('tasks');
    wasPaying.current = paying;
  }, [paying, view, go]);
}

export function AppShell({ layoutOverride }: { layoutOverride?: Layout }) {
  const { t } = useTranslation();
  const phase = useAppState((s) => s.phase);
  const needsOnboarding = useAppState((s) => !!s.profile && !s.profile.onboarding_done);
  const auth = useStore().backend.auth;
  const recovering = useSyncExternalStore(auth.onPasswordRecovery, auth.inPasswordRecovery);
  const services = useServices();
  const wide = useIsWide();
  const [view, setView] = useState<View>('tasks');
  usePlansFirst(setView);
  useLeavePlansWhenPaid(view, setView);
  const layout: Layout = layoutOverride ?? (services.desktop ? 'desktop' : wide ? 'wide' : 'mobile');

  if (phase === 'loading') {
    return (
      <div className="splash" aria-busy="true">
        <BeeLogo size={48} />
        <span className="sr-only">{t('common.loading')}</span>
      </div>
    );
  }
  if (phase === 'signedOut') {
    return (
      <>
        <AuthScreens />
        <Toast />
      </>
    );
  }
  if (recovering) {
    return (
      <>
        <NewPasswordScreen />
        <Toast />
      </>
    );
  }

  return (
    <NavContext.Provider value={{ view, go: setView }}>
      <div className={cls('app', `app--${layout}`)} data-layout={layout}>
        {layout === 'wide' && <Sidebar />}
        <main className="app__main">
          <OfflineBanner />
          {layout === 'mobile' && !services.desktop && view === 'tasks' && <InstallAppBanner />}
          {view === 'tasks' && <TasksView layout={layout} />}
          {view === 'focus' && <FocusView />}
          {view === 'settings' && <SettingsScreen onBack={() => setView('tasks')} />}
          {view === 'subscription' && <Paywall onBack={() => setView('tasks')} />}
        </main>
        {layout === 'mobile' && <MobileTabBar />}
        <Toast />
        {!needsOnboarding && <DayDone />}
        {!needsOnboarding && <SubscriptionEnded />}
        {needsOnboarding && <Onboarding />}
      </div>
    </NavContext.Provider>
  );
}
